import React, { useState, useEffect, useRef } from 'react';
import { 
  FaUpload, FaTrash, FaEye, FaFilePdf, 
  FaPlus, FaMinus, FaGraduationCap, 
  FaIdCard, FaTimes,
  FaSearch, FaPlusCircle, FaChevronDown,
  FaCheck, FaUniversity, FaFile, FaFileImage, FaFileWord
} from 'react-icons/fa';
import { instituteService, qualificationService } from '../Api/documentService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createPortal } from "react-dom";
import ReactDOM from 'react-dom';

// Enhanced SearchableDropdown Component (keep as is - no changes needed)
// Update the SearchableDropdown component in DocumentsSection.jsx
const SearchableDropdown = ({ 
  value, 
  onChange, 
  options = [], 
  placeholder = "Search or select institute",
  loading = false,
  onAddNew,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newInstituteName, setNewInstituteName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.instName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsEditing(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get display value
  const displayValue = value 
    ? options.find(o => o.instId === value)?.instName || searchTerm
    : searchTerm;

  // Handle input focus - enable editing
  const handleInputFocus = () => {
    setIsEditing(true);
    setIsOpen(true);
  };

  // Handle input blur
  const handleInputBlur = () => {
    setTimeout(() => {
      if (!isOpen) {
        setIsEditing(false);
      }
    }, 200);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    
    // If input is cleared or doesn't match any option, clear the selection
    if (!newValue.trim() || !options.some(opt => opt.instName === newValue)) {
      onChange(null, '');
    }
    
    setIsOpen(true);
  };

  // Handle option selection
  const handleSelect = (option) => {
    onChange(option.instId, option.instName);
    setSearchTerm(option.instName);
    setIsOpen(false);
    setIsEditing(false);
  };

  // Handle key down for backspace
  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && !searchTerm && value) {
      // Clear selection on backspace when input is empty
      onChange(null, '');
      setSearchTerm('');
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setIsEditing(false);
    }
  };

  return (
    <div 
      ref={dropdownRef}
      className="searchable-dropdown"
      style={{ position: 'relative', width: '100%' }}
    >
      {/* Main Input */}
      <div 
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          backgroundColor: disabled ? '#f3f4f6' : 'white',
          border: '2px solid #86efac',
          fontSize: '1.4rem',
          color: '#374151',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={searchTerm || displayValue}
          placeholder={placeholder}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(true);
          }}
          disabled={disabled}
          style={{
            border: 'none',
            outline: 'none',
            width: '100%',
            fontSize: '1.4rem',
            backgroundColor: 'transparent',
            color: '#374151',
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
        <FaChevronDown 
          style={{
            color: '#22c55e',
            transition: 'transform 0.3s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)'
          }}
        />
      </div>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div 
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '2px solid #86efac',
            borderRadius: '8px',
            marginTop: '4px',
            maxHeight: '300px',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            animation: 'slideDown 0.2s ease-out'
          }}
        >
          {/* Loading State */}
          {loading ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
              Loading institutes...
            </div>
          ) : filteredOptions.length > 0 ? (
            // Options List
            filteredOptions.map((option) => (
              <div
                key={option.instId}
                onClick={() => handleSelect(option)}
                style={{
                  padding: '0.75rem 1rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: value === option.instId ? '#dcfce7' : 'transparent',
                  borderLeft: value === option.instId ? '4px solid #22c55e' : '4px solid transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = value === option.instId ? '#dcfce7' : '#f0fdf4';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = value === option.instId ? '#dcfce7' : 'transparent';
                }}
              >
                <FaUniversity style={{ color: '#22c55e', fontSize: '1.3rem' }} />
                <span style={{ fontSize: '1.4rem' }}>{option.instName}</span>
                {value === option.instId && (
                  <FaCheck style={{ marginLeft: 'auto', color: '#22c55e' }} />
                )}
              </div>
            ))
          ) : searchTerm ? (
            // No Results Found - Show Add Option
            <div style={{ padding: '1rem' }}>
              <div style={{ textAlign: 'center', color: '#6b7280', marginBottom: '0.75rem' }}>
                No institutes found for "{searchTerm}"
              </div>
              <button
                onClick={() => setShowAddModal(true)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontSize: '1.4rem',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
              >
                <FaPlusCircle /> Add "{searchTerm}" as new institute
              </button>
            </div>
          ) : (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
              Start typing to search institutes
            </div>
          )}

          {/* Add Institute Modal */}
          {showAddModal && (
            <div 
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                zIndex: 1001
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0, color: '#166534' }}>Add New Institute</h4>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewInstituteName('');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6b7280',
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    padding: '0.25rem'
                  }}
                >
                  <FaTimes />
                </button>
              </div>
              
              <input
                type="text"
                value={newInstituteName}
                onChange={(e) => setNewInstituteName(e.target.value)}
                placeholder="Enter institute name"
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #86efac',
                  borderRadius: '6px',
                  fontSize: '1.4rem',
                  marginBottom: '1rem'
                }}
              />
              
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={async () => {
                    if (!newInstituteName.trim()) return;
                    
                    setIsAdding(true);
                    try {
                      const newInstitute = await onAddNew(newInstituteName);
                      onChange(newInstitute.instId, newInstitute.instName);
                      setSearchTerm(newInstitute.instName);
                      setShowAddModal(false);
                      setNewInstituteName('');
                    } catch (error) {
                      console.error('Failed to add institute:', error);
                    } finally {
                      setIsAdding(false);
                    }
                  }}
                  disabled={isAdding || !newInstituteName.trim()}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    backgroundColor: isAdding ? '#9ca3af' : '#22c55e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isAdding ? 'not-allowed' : 'pointer',
                    fontSize: '1.4rem',
                    fontWeight: '500'
                  }}
                >
                  {isAdding ? 'Adding...' : 'Add Institute'}
                </button>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewInstituteName('');
                  }}
                  style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '1.4rem'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message,
  confirmText = "Delete",
  cancelText = "Cancel",
  type = "delete"
}) => {
  const modalRef = useRef(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [portalElement, setPortalElement] = useState(null);

  // Create portal element on mount
  useEffect(() => {
    // Create a div for the portal
    const el = document.createElement('div');
    el.id = 'modal-portal';
    document.body.appendChild(el);
    setPortalElement(el);

    return () => {
      // Clean up on unmount
      if (el && document.body.contains(el)) {
        document.body.removeChild(el);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleConfirm = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onConfirm();
    }, 200);
  };

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getModalStyles = () => {
    switch(type) {
      case 'delete':
        return {
          headerBg: '#fee2e2',
          headerColor: '#dc2626',
          confirmBg: '#dc2626',
          confirmHoverBg: '#b91c1c',
          icon: <FaTrash style={{ color: '#dc2626', fontSize: '2rem' }} />
        };
      case 'warning':
        return {
          headerBg: '#fef3c7',
          headerColor: '#d97706',
          confirmBg: '#f59e0b',
          confirmHoverBg: '#d97706',
          icon: <FaTimes style={{ color: '#d97706', fontSize: '2rem' }} />
        };
      case 'info':
        return {
          headerBg: '#dbeafe',
          headerColor: '#2563eb',
          confirmBg: '#3b82f6',
          confirmHoverBg: '#2563eb',
          icon: <FaCheck style={{ color: '#2563eb', fontSize: '2rem' }} />
        };
      default:
        return {
          headerBg: '#fee2e2',
          headerColor: '#dc2626',
          confirmBg: '#dc2626',
          confirmHoverBg: '#b91c1c',
          icon: <FaTrash style={{ color: '#dc2626', fontSize: '2rem' }} />
        };
    }
  };

  const styles = getModalStyles();

  // Create the modal content
  const modalContent = isOpen || isAnimating ? (
    <>
      {/* Overlay */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
          opacity: isAnimating ? 1 : 0,
          transition: 'opacity 0.2s ease-out',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)'
        }}
      />

      {/* Modal */}
      <div 
        ref={modalRef}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: isAnimating 
            ? 'translate(-50%, -50%) scale(1)' 
            : 'translate(-50%, -50%) scale(0.9)',
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          opacity: isAnimating ? 1 : 0,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 9999
        }}
      >
        {/* Header */}
        <div style={{
          backgroundColor: styles.headerBg,
          padding: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          borderBottom: `1px solid ${styles.headerColor}20`,
          flexShrink: 0
        }}>
          {styles.icon}
          <h3 style={{ 
            margin: 0, 
            color: styles.headerColor, 
            fontSize: '1.5rem',
            fontWeight: 600 
          }}>
            {title}
          </h3>
          <button
            onClick={handleClose}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: '#6b7280',
              cursor: 'pointer',
              fontSize: '1.5rem',
              padding: '0.25rem',
              borderRadius: '4px',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div style={{ 
          padding: '2rem 1.5rem',
          flex: 1,
          overflowY: 'auto'
        }}>
          <p style={{ 
            margin: 0, 
            fontSize: '1.4rem', 
            lineHeight: 1.6,
            color: '#374151',
            textAlign: 'center'
          }}>
            {message}
          </p>
        </div>

        {/* Footer */}
        <div style={{
          padding: '1.5rem',
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end',
          borderTop: '1px solid #e5e7eb',
          flexShrink: 0
        }}>
          <button
            onClick={handleClose}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1.4rem',
              fontWeight: 500,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: styles.confirmBg,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1.4rem',
              fontWeight: 500,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.confirmHoverBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.confirmBg;
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </>
  ) : null;

  // Use portal to render outside the normal DOM hierarchy
  if (!portalElement) return null;
  return ReactDOM.createPortal(modalContent, portalElement);
};



// Document Card Component - Fixed for ID and Bank documents
const DocumentCard = ({ doc, onUpload, onDelete, onView, cardType }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const getCardStyles = (type) => {
    switch(type) {
      case 'id':
        return {
          borderColor: '#e9d5ff',
          bgColor: '#faf5ff',
          iconColor: '#8b5cf6',
          hoverBorder: '#c4b5fd'
        };
      case 'bank':
        return {
          borderColor: '#bae6fd',
          bgColor: '#f0f9ff',
          iconColor: '#0ea5e9',
          hoverBorder: '#7dd3fc'
        };
      case 'qualification':
        return {
          borderColor: '#bbf7d0',
          bgColor: '#f0fdf4',
          iconColor: '#22c55e',
          hoverBorder: '#86efac'
        };
      default:
        return {
          borderColor: '#e5e7eb',
          bgColor: '#f9fafb',
          iconColor: '#6b7280',
          hoverBorder: '#d1d5db'
        };
    }
  };

  const styles = getCardStyles(cardType);

  const handleUploadClick = () => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.jpg,.jpeg,.png,.doc,.docx";
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        setIsUploading(true);
        try {
          await onUpload(doc.documentType, file);
        } catch (error) {
          toast.error(`Failed to upload ${doc.documentType}`);
        } finally {
          setIsUploading(false);
        }
      }
    };
    input.click();
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    setIsDeleting(true);
    try {
      await onDelete(doc.documentId);
      // Success toast will be handled by parent
    } catch (error) {
      // Error toast will be handled by parent
    } finally {
      setIsDeleting(false);
    }
  };

  const getFileIcon = () => {
    if (doc.fileName?.includes('.pdf')) return <FaFilePdf />;
    return <FaFile />;
  };

  return (
    <>
      <div 
        className="doc-section-card"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          border: `2px solid ${isHovered ? styles.hoverBorder : styles.borderColor}`,
          backgroundColor: styles.bgColor,
          borderRadius: '12px',
          padding: '1.5rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
          boxShadow: isHovered ? '0 8px 30px rgba(0, 0, 0, 0.1)' : 'none'
        }}
      >
        <div className="doc-section-card-header">
          <div 
            className="doc-section-icon" 
            style={{ 
              color: styles.iconColor,
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              background: 'rgba(255, 255, 255, 0.7)',
              transform: isHovered ? 'scale(1.1)' : 'scale(1)',
              transition: 'transform 0.3s ease'
            }}
          >
            {getFileIcon()}
          </div>
          <h4 style={{ margin: 0, fontSize: '1.5rem', color: '#1f2937', fontWeight: 600 }}>
            {doc.documentType}
          </h4>
        </div>
        
        <div className="doc-section-card-body" style={{ flex: 1, marginBottom: '1.25rem' }}>
          {doc.url ? (
            <div className="doc-section-uploaded">
              <span 
                className="doc-section-status-success"
                style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  backgroundColor: '#dcfce7',
                  color: '#166534',
                  borderRadius: '20px',
                  fontSize: '1.1rem',
                  fontWeight: 500
                }}
              >
                ✓ Uploaded
              </span>
              {doc.fileName && (
                <p 
                  className="doc-section-filename"
                  style={{ margin: '0.5rem 0 0', fontSize: '1.4rem', color: '#6b7280', wordBreak: 'break-all' }}
                >
                  {doc.fileName}
                </p>
              )}
            </div>
          ) : (
            <div className="doc-section-missing">
              <span 
                className="doc-section-status-missing"
                style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  backgroundColor: '#fef3c7',
                  color: '#92400e',
                  borderRadius: '20px',
                  fontSize: '1.1rem',
                  fontWeight: 500
                }}
              >
                Pending
              </span>
            </div>
          )}
        </div>
        
        <div className="doc-section-card-actions">
          {!doc.url ? (
            <button 
              className="doc-section-btn-upload"
              onClick={handleUploadClick}
              disabled={isUploading}
              style={{ 
                backgroundColor: styles.iconColor,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '0.75rem',
                border: 'none',
                borderRadius: '10px',
                color: 'white',
                fontWeight: 500,
                cursor: isUploading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                fontSize: '1.1rem',
                opacity: isUploading ? 0.6 : 1
              }}
            >
              <FaUpload /> 
              {isUploading ? 'Uploading...' : 'Upload'}
            </button>
          ) : (
            <div className="doc-section-action-btns" style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
              <button 
                className="doc-section-btn-view"
                onClick={() => onView(doc)}
                style={{ 
                  borderColor: styles.iconColor, 
                  color: styles.iconColor,
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  background: 'transparent',
                  border: '2px solid',
                  borderRadius: '10px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '1.1rem',
                }}
              >
                <FaEye /> View
              </button>
              <button 
                className="doc-section-btn-delete"
                onClick={handleDelete}
                disabled={isDeleting}
                style={{ 
                  backgroundColor: '#ef4444',
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  border: 'none',
                  borderRadius: '10px',
                  color: 'white',
                  fontWeight: 500,
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '1.1rem',
                  opacity: isDeleting ? 0.6 : 1
                }}
              >
                <FaTrash /> 
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal for Document Deletion */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Document"
        message={`Are you sure you want to delete your ${doc.documentType} document? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="delete"
      />
    </>
  );
};

// Qualification Row Component for adding NEW qualifications
const QualificationRow = ({ 
  qualification, 
  index, 
  onUpdate, 
  onDelete,
  institutes = [],
  loadingInstitutes = false,
  onAddInstitute
}) => {
  const [localData, setLocalData] = useState(qualification);
  const [isRowHovered, setIsRowHovered] = useState(false);
  
  const qualificationOptions = [
    { value: 'Post Graduation', label: 'Post Graduation' },
    { value: 'Graduation', label: 'Graduation' },
    { value: '12', label: '12th / HSC' },
    { value: '10', label: '10th / SSC' }
  ];

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 1970; year--) {
      years.push(year);
    }
    return years;
  };

  const handleChange = (field, value, instName = '') => {
    const updated = { ...localData, [field]: value };
    if (field === 'instituteId' && instName) {
      updated.institute = instName;
    }
    setLocalData(updated);
    onUpdate(index, updated);
  };

  const handleInstituteChange = (instId, instName) => {
    const updated = { 
      ...localData, 
      instituteId: instId,
      institute: instName 
    };
    setLocalData(updated);
    onUpdate(index, updated);
  };

const handleFileUpload = (e) => {
  const file = e.target.files[0];
  if (file) {
    console.log('📁 File selected:', {
      name: file.name,
      size: file.size,
      type: file.type,
      constructor: file.constructor.name,
      isFile: file instanceof File
    });
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    const updated = { 
      ...localData, 
      attachment: file,  // Store the actual File object
      fileName: file.name 
    };
    console.log('✅ Updated qualification with file:', updated);
    setLocalData(updated);
    onUpdate(index, updated);
  }
};

  const handleFileRemove = () => {
    const updated = { ...localData, attachment: null, fileName: null };
    setLocalData(updated);
    onUpdate(index, updated);
  };

  const requiresDropdown = ['Post Graduation', 'Graduation'].includes(localData.type);

  return (
    <div 
      className="qualification-row" 
      onMouseEnter={() => setIsRowHovered(true)}
      onMouseLeave={() => setIsRowHovered(false)}
      style={{
        border: '2px solid #bbf7d0',
        backgroundColor: '#f0fdf4',
        borderRadius: '12px',
        padding: '1.5rem',
        paddingTop: '4rem',
        marginBottom: '1.5rem',
        position: 'relative',
        transition: 'all 0.3s ease',
        boxShadow: isRowHovered ? '0 8px 25px rgba(187, 247, 208, 0.3)' : 'none',
        animation: 'slideIn 0.3s ease-out'
      }}
    >
      <button 
        className="qualification-delete-row"
        onClick={() => onDelete(index)}
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          background: 'linear-gradient(135deg, #fecaca, #fca5a5)',
          border: 'none',
          borderRadius: '50%',
          width: '36px !important' ,
          minWidth: '36px !important' ,
          height: '36px !important',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#dc2626',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: '0 2px 4px rgba(220, 38, 38, 0.1)',
          zIndex: 10,
        }}
      >
        <FaMinus style={{ fontSize: '1.1rem' }} />
      </button>

      <div 
        className="qualification-form-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.25rem'
        }}
      >
        {/* Qualification Type */}
        <div className="form-field" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={{ fontSize: '1.5rem', fontWeight: 600, color: '#374151' }}>Qualification*</label>
          <select 
            value={localData.type || ''}
            onChange={(e) => handleChange('type', e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              backgroundColor: 'white',
              fontSize: '1.5rem',
              color: '#374151',
              border: '2px solid #86efac',
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2322c55e'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center',
              backgroundSize: '1.5em',
            }}
          >
            <option value="" style={{ fontSize: '1.5rem', color: '#9ca3af' }}>Select Qualification</option>
            {qualificationOptions.map(opt => (
              <option 
                key={opt.value} 
                value={opt.value}
                style={{ fontSize: '1.5rem', padding: '0.5rem' }}
              >
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Institute */}
        <div className="form-field" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={{ fontSize: '1.5rem', fontWeight: 600, color: '#374151' }}>Institute*</label>
          {requiresDropdown ? (
            <SearchableDropdown
              value={localData.instituteId}
              onChange={handleInstituteChange}
              options={institutes}
              placeholder="Search or select institute"
              loading={loadingInstitutes}
              onAddNew={onAddInstitute}
            />
          ) : (
            <input 
              type="text"
              placeholder="Enter institute name"
              value={localData.institute || ''}
              onChange={(e) => handleChange('institute', e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                backgroundColor: 'white',
                fontSize: '1.5rem',
                color: '#374151',
                border: '2px solid #86efac',
              }}
            />
          )}
        </div>

        {/* Year */}
        <div className="form-field" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={{ fontSize: '1.5rem', fontWeight: 600, color: '#374151' }}>Year*</label>
          <select 
            value={localData.year || ''}
            onChange={(e) => handleChange('year', e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              backgroundColor: 'white',
              fontSize: '1.5rem',
              color: '#374151',
              border: '2px solid #86efac',
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2322c55e'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center',
              backgroundSize: '1.5em',
            }}
          >
            <option style={{ fontSize: '1.5rem', color: '#9ca3af' }} value="">Select Year</option>
            {generateYearOptions().map(year => (
              <option 
                key={year} 
                value={year}
                style={{ fontSize: '1.5rem', padding: '0.5rem' }}
              >
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Grade */}
        <div className="form-field" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={{ fontSize: '1.5rem', fontWeight: 600, color: '#374151' }}>Grade/Marks*</label>
          <input 
            type="text"
            placeholder="Enter grade or marks"
            value={localData.grade || ''}
            onChange={(e) => handleChange('grade', e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              backgroundColor: 'white',
              fontSize: '1.5rem',
              color: '#374151',
              border: '2px solid #86efac',
            }}
          />
        </div>

        {/* Attachment */}
        <div className="form-field" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={{ fontSize: '1.5rem', fontWeight: 600, color: '#374151' }}>Attachment*</label>
          <div 
            className="file-upload-area" 
            onClick={() => !localData.fileName && document.getElementById(`qual-file-${index}`)?.click()}
            style={{
              border: '2px dashed #86efac',
              borderRadius: '8px',
              padding: '1rem',
              textAlign: 'center',
              backgroundColor: '#f0fdf4',
              cursor: !localData.fileName ? 'pointer' : 'default',
              transition: 'all 0.3s',
            }}
          >
            {!localData.fileName ? (
              <>
                <input
                  type="file"
                  id={`qual-file-${index}`}
                  onChange={handleFileUpload}
                  accept=".pdf,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                />
                <div style={{ cursor: 'pointer' }}>
                  <FaUpload style={{ fontSize: '1.5rem', color: '#22c55e', marginBottom: '0.5rem' }} />
                  <p style={{ fontSize: '1.5rem', margin: '0', color: '#374151' }}>Click to upload</p>
                  <p style={{ fontSize: '1.1rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    PDF, JPG, PNG (Max 5MB)
                  </p>
                </div>
              </>
            ) : (
              <div className="file-preview">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FaFilePdf style={{ color: '#ef4444', fontSize: '1.5rem' }} />
                    <span style={{ fontSize: '1.4rem' }}>{localData.fileName}</span>
                  </div>
                  <button 
                    type="button"
                    onClick={handleFileRemove}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      fontSize: '1.4rem',
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Qualification Card for DISPLAYING uploaded qualifications
const UploadedQualificationCard = ({ qualification, onDelete }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    setIsDeleting(true);
    try {
      const success = await onDelete(qualification.qualId);
      if (success) {
        toast.success('Qualification deleted successfully!');
      } else {
        toast.error('Failed to delete qualification');
      }
    } catch (error) {
      toast.error('Failed to delete qualification');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleView = () => {
    if (qualification.qualDoc) {
      window.open(qualification.qualDoc, '_blank');
    }
  };

  const getQualificationColor = (type) => {
    switch(type) {
      case 'Post Graduation': return '#9333ea';
      case 'Graduation': return '#3b82f6';
      case '12': return '#10b981';
      case '10': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const color = getQualificationColor(qualification.qualType);

  return (
    <>
      <div 
        style={{
          border: `2px solid ${color}`,
          backgroundColor: `${color}10`,
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1rem',
          transition: 'all 0.3s ease'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={{
                backgroundColor: color,
                color: 'white',
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.2rem'
              }}>
                <FaGraduationCap />
              </div>
              <div>
                <h4 style={{ margin: 0, color: '#1f2937', fontSize: '1.4rem' }}>
                  {qualification.qualType}
                </h4>
                <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '1.2rem' }}>
                  {qualification.qualInstitute} ({qualification.qualYear})
                </p>
                {qualification.qualGrade && (
                  <p style={{ margin: '0.25rem 0 0', color: '#374151', fontSize: '1.2rem' }}>
                    Grade: <strong>{qualification.qualGrade}</strong>
                  </p>
                )}
              </div>
            </div>
            
            {qualification.qualDoc && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e5e7eb'
              }}>
                <FaFilePdf style={{ color: '#ef4444' }} />
                <span style={{ fontSize: '1.2rem', color: '#6b7280' }}>
                  Document uploaded
                </span>
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {qualification.qualDoc && (
              <button 
                onClick={handleView}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'transparent',
                  border: `1px solid ${color}`,
                  color: color,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaEye /> View
              </button>
            )}
            <button 
              onClick={handleDelete}
              disabled={isDeleting}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                fontSize: '1.1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <FaTrash /> {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Qualification Deletion */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Qualification"
        message={`Are you sure you want to delete your ${qualification.qualType} qualification? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="delete"
      />
    </>
  );
};

// Main Documents Section Component
const DocumentsSection = ({ 
  userId, 
  organisationId, 
  documents, 
  documentsLoading,
  onUploadDocument, 
  onDeleteDocument, 
  onViewDocument,
    onSaveQualifications,  // Add this prop to pass qualification saving to parent
  isHRMode = false,  
}) => {
  // Filter documents for ID and Bank sections
  const idDocuments = documents.filter(doc => 
    ['Aadhaar Card', 'PAN Card', 'Passport'].includes(doc.documentType)
  );
  
  const bankDocuments = documents.filter(doc => 
    ['Cancelled Cheque', 'Bank Passbook', 'Bank Statement'].includes(doc.documentType)
  );

  // State for NEW qualifications being added
  const [qualifications, setQualifications] = useState([
    { type: '', institute: '', instituteId: '', year: '', grade: '', attachment: null, fileName: null }
  ]);

  // State for EXISTING uploaded qualifications
  const [uploadedQualifications, setUploadedQualifications] = useState([]);
  const [loadingQualifications, setLoadingQualifications] = useState(false);

  const [institutes, setInstitutes] = useState([]);
  const [loadingInstitutes, setLoadingInstitutes] = useState(false);
  const [savingQualifications, setSavingQualifications] = useState(false);

  // Fetch institutes
  useEffect(() => {
    let isMounted = true;
    
    const fetchInstitutes = async () => {
      if (!isMounted) return;
      
      setLoadingInstitutes(true);
      try {
        const data = await instituteService.getInstitutes();
        if (isMounted) {
          setInstitutes(data);
        }
      } catch (error) {
        console.error('Error fetching institutes:', error);
      } finally {
        if (isMounted) {
          setLoadingInstitutes(false);
        }
      }
    };

    fetchInstitutes();
    return () => { isMounted = false; };
  }, []);

  // Fetch existing qualifications when userId changes
  useEffect(() => {
    const fetchExistingQualifications = async () => {
      if (!userId) return;
      
      setLoadingQualifications(true);
      try {
        const response = await qualificationService.getUserQualifications(userId);
        if (response.success && Array.isArray(response.qualifications)) {
          setUploadedQualifications(response.qualifications);
        }
      } catch (error) {
        console.error('Error fetching qualifications:', error);
      } finally {
        setLoadingQualifications(false);
      }
    };

    fetchExistingQualifications();
  }, [userId]);

  // Handle adding new institute
  const handleAddInstitute = async (instituteName) => {
    try {
      const newInstitute = await instituteService.addInstitute(instituteName);
      setInstitutes(prev => [...prev, newInstitute]);
      return newInstitute;
    } catch (error) {
      console.error('Error adding institute:', error);
      throw error;
    }
  };

  // Handle ID/Bank document upload
  const handleIDUpload = async (docType, file) => {
    try {
      await onUploadDocument(docType, file);
    } catch (error) {
      // Error is handled by parent component
    }
  };

  const handleBankUpload = async (docType, file) => {
    try {
      await onUploadDocument(docType, file);
    } catch (error) {
      // Error is handled by parent component
    }
  };

  const handleIDDelete = async (docId) => {
    try {
      await onDeleteDocument(docId);
    } catch (error) {
      // Error is handled by parent component
    }
  };

  const handleBankDelete = async (docId) => {
    try {
      await onDeleteDocument(docId);
    } catch (error) {
      // Error is handled by parent component
    }
  };

  // Handle NEW qualifications form
  const handleAddQualification = () => {
    setQualifications(prev => [...prev, 
      { type: '', institute: '', instituteId: '', year: '', grade: '', attachment: null, fileName: null }
    ]);
  };

  const handleUpdateQualification = (index, updatedData) => {
    setQualifications(prev => prev.map((qual, i) => 
      i === index ? updatedData : qual
    ));
  };

  const handleDeleteQualificationRow = (index) => {
    if (qualifications.length > 1) {
      setQualifications(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Handle EXISTING qualification deletion
  const handleDeleteUploadedQualification = async (qualId) => {
    try {
      const response = await qualificationService.deleteQualification(qualId);
      if (response.success) {
        setUploadedQualifications(prev => prev.filter(q => q.qualId !== qualId));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting qualification:', error);
      return false;
    }
  };

 const handleSaveQualifications = async () => {
  // If parent provided a save function (Update mode), use it
  if (onSaveQualifications) {
    // Log what we're trying to save
    console.log('📋 Qualifications to save:', qualifications);
    
    const qualificationsData = qualifications
      .filter(qual => {
        // Debug each qualification
        console.log(`🔍 Checking qualification:`, {
          type: qual.type,
          institute: qual.institute,
          year: qual.year,
          attachment: qual.attachment,
          fileName: qual.fileName,
          hasAttachment: !!qual.attachment,
          attachmentType: qual.attachment?.constructor?.name
        });
        
        const isValid = qual.type && qual.institute && qual.year && qual.attachment;
        console.log(`✅ Qualification valid: ${isValid}`);
        return isValid;
      })
      .map(qual => {
        console.log(`📦 Mapping qualification:`, qual);
        return {
          qualificationType: qual.type,
          institute: qual.institute,
          year: qual.year.toString(),
          grade: qual.grade || '',
          attachment: qual.attachment  // This should be a File object
        };
      });

    console.log(`📊 Final qualifications data:`, qualificationsData);
    
    if (qualificationsData.length === 0) {
      toast.warning('Please add at least one valid qualification with all required fields and a file.');
      
      // Show which qualifications are missing what
      qualifications.forEach((qual, index) => {
        const missingFields = [];
        if (!qual.type) missingFields.push('Qualification Type');
        if (!qual.institute) missingFields.push('Institute');
        if (!qual.year) missingFields.push('Year');
        if (!qual.attachment) missingFields.push('Attachment/File');
        
        if (missingFields.length > 0) {
          console.warn(`❌ Qualification ${index + 1} missing: ${missingFields.join(', ')}`);
        }
      });
      
      return;
    }

    setSavingQualifications(true);
    
    try {
      console.log('🚀 Sending qualifications to parent...');
      await onSaveQualifications(qualificationsData);
      
      console.log('✅ Qualifications saved successfully!');
      
      // Refresh existing qualifications if userId is available
      if (userId) {
        console.log('🔄 Refreshing qualifications list...');
        const refreshResponse = await qualificationService.getUserQualifications(userId);
        if (refreshResponse.success && Array.isArray(refreshResponse.qualifications)) {
          setUploadedQualifications(refreshResponse.qualifications);
        }
      }
      
      // Clear the form
      setQualifications([{ 
        type: '', 
        institute: '', 
        instituteId: '', 
        year: '', 
        grade: '',
        attachment: null, 
        fileName: null 
      }]);
      
    } catch (error) {
      console.error('❌ Save qualifications error:', error);
      console.error('❌ Error stack:', error.stack);
      // Error toast handled by parent
    } finally {
      setSavingQualifications(false);
    }
    } else {
      // Profile mode - use localStorage
      const userDetailsStr = localStorage.getItem('userDetails');
      if (!userDetailsStr) {
        toast.error('Please log in first');
        return;
      }

      const qualificationsData = qualifications
        .filter(qual => qual.type && qual.institute && qual.year && qual.attachment)
        .map(qual => ({
          qualificationType: qual.type,
          institute: qual.institute,
          year: qual.year.toString(),
          grade: qual.grade || '',
          attachment: qual.attachment
        }));

      if (qualificationsData.length === 0) {
        toast.warning('Please add at least one valid qualification with all required fields and a file.');
        return;
      }

      setSavingQualifications(true);
      
      try {
        const response = await qualificationService.uploadQualificationsBulk(qualificationsData);
        
        if (response.success) {
          toast.success(`${response.totalProcessed} qualification(s) saved successfully!`);
          
          // Refresh existing qualifications
          const userDetails = JSON.parse(userDetailsStr);
          if (userDetails.userId) {
            const refreshResponse = await qualificationService.getUserQualifications(userDetails.userId);
            if (refreshResponse.success && Array.isArray(refreshResponse.qualifications)) {
              setUploadedQualifications(refreshResponse.qualifications);
            }
          }
          
          // Clear the form
          setQualifications([{ 
            type: '', 
            institute: '', 
            instituteId: '', 
            year: '', 
            grade: '',
            attachment: null, 
            fileName: null 
          }]);
        } else {
          if (response.errors && response.errors.length > 0) {
            toast.error(`Some errors occurred:\n${response.errors.join('\n')}`);
          }
        }
      } catch (error) {
        console.error('Save qualifications error:', error);
        toast.error(`Failed to save qualifications: ${error.message}`);
      } finally {
        setSavingQualifications(false);
      }
    }
  };


  // Refresh qualifications
  const refreshQualifications = async () => {
    if (!userId) return;
    
    setLoadingQualifications(true);
    try {
      const response = await qualificationService.getUserQualifications(userId);
      if (response.success && Array.isArray(response.qualifications)) {
        setUploadedQualifications(response.qualifications);
        toast.success('Qualifications refreshed successfully!');
      }
    } catch (error) {
      console.error('Error refreshing qualifications:', error);
      toast.error('Failed to refresh qualifications');
    } finally {
      setLoadingQualifications(false);
    }
  };

  // Loading state
  if (documentsLoading) {
    return (
      <div className="documents-section-container">
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '2rem', color: '#6b7280' }}>Loading documents...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="documents-section-container">
      {/* Self IDs Section */}
      <div className="documents-subsection">
        <div className="subsection-header" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '2.5rem',
          paddingBottom: '0.75rem',
          borderBottom: '2px solid #e9d5ff'
        }}>
          <div className="subsection-icon" style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: '#f3e8ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#8b5cf6'
          }}>
            <FaIdCard size={24} />
          </div>
          <div>
            <h3 style={{ margin: 0, color: '#4c1d95', fontSize: '1.5rem' }}>ID Documents</h3>
            <p style={{ margin: '0.85rem 0 0', color: '#6b7280', fontSize: '1.5rem' }}>
              Upload your identification documents
            </p>
          </div>
        </div>
        
        <div className="documents-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1.5rem',
          marginBottom: '4rem'
        }}>
          {idDocuments.map(doc => (
            <DocumentCard
              key={doc.documentId}
              doc={doc}
              onUpload={handleIDUpload}
              onDelete={handleIDDelete}
              onView={onViewDocument}
              cardType="id"
            />
          ))}
        </div>
      </div>

      {/* Bank Documents Section */}
      <div className="documents-subsection">
        <div className="subsection-header" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '2.5rem',
          paddingBottom: '0.75rem',
          borderBottom: '2px solid #bae6fd'
        }}>
          <div className="subsection-icon" style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: '#e0f2fe',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#0ea5e9'
          }}>
            <FaIdCard size={24} />
          </div>
          <div>
            <h3 style={{ margin: 0, color: '#075985', fontSize: '1.5rem' }}>Bank Documents</h3>
            <p style={{ margin: '0.85rem 0 0', color: '#6b7280', fontSize: '1.5rem' }}>
              Upload your banking documents
            </p>
          </div>
        </div>
        
        <div className="documents-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1.5rem',
          marginBottom: '4rem'
        }}>
          {bankDocuments.map(doc => (
            <DocumentCard
              key={doc.documentId}
              doc={doc}
              onUpload={handleBankUpload}
              onDelete={handleBankDelete}
              onView={onViewDocument}
              cardType="bank"
            />
          ))}
        </div>
      </div>

      {/* Qualifications Section */}
      <div className="documents-subsection">
        <div className="subsection-header" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '2.5rem',
          paddingBottom: '0.75rem',
          borderBottom: '3px solid #bbf7d0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div className="subsection-icon" style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              backgroundColor: '#dcfce7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#22c55e'
            }}>
              <FaGraduationCap size={24} />
            </div>
            <div>
              <h3 style={{ margin: 0, color: '#166534', fontSize: '1.5rem' }}>Qualifications</h3>
              <p style={{ margin: '0.85rem 0 0', color: '#6b7280', fontSize: '1.5rem' }}>
                Add your educational qualifications
              </p>
            </div>
          </div>
          
          <div className="qualification-actions" style={{ display: 'flex', gap: '0.75rem' }}>
            <button 
              onClick={refreshQualifications}
              disabled={loadingQualifications}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.25rem',
                backgroundColor: loadingQualifications ? '#d1d5db' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loadingQualifications ? 'not-allowed' : 'pointer',
                fontSize: '1.4rem',
                fontWeight: '500',
              }}
            >
              {loadingQualifications ? 'Refreshing...' : 'Refresh'}
            </button>
            <button 
              onClick={handleAddQualification}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.25rem',
                backgroundColor: '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1.4rem',
                fontWeight: '500',
              }}
            >
              <FaPlus /> Add Qualification
            </button>
            
            <button 
              onClick={handleSaveQualifications}
              disabled={savingQualifications}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.25rem',
                backgroundColor: savingQualifications ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: savingQualifications ? 'not-allowed' : 'pointer',
                fontSize: '1.4rem',
                fontWeight: '500',
              }}
            >
              {savingQualifications ? 'Saving...' : 'Save Qualifications'}
            </button>
          </div>
        </div>

          <div className="qualifications-list">
          {qualifications.map((qual, index) => (
            <QualificationRow
              key={index}
              qualification={qual}
              index={index}
              onUpdate={handleUpdateQualification}
              onDelete={handleDeleteQualificationRow}
              institutes={institutes}
              loadingInstitutes={loadingInstitutes}
              onAddInstitute={handleAddInstitute}
            />
          ))}
        </div>
        
        {/* Display Existing Qualifications */}
        {uploadedQualifications.length > 0 && (
          <div style={{ marginBottom: '3rem' }}>
            <h4 style={{ fontSize: '1.4rem', color: '#374151', marginBottom: '1rem' }}>
              Uploaded Qualifications ({uploadedQualifications.length})
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {uploadedQualifications.map((qual) => (
                <UploadedQualificationCard
                  key={qual.qualId}
                  qualification={qual}
                  onDelete={handleDeleteUploadedQualification}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Add New Qualification Form */}
      
      </div>
    </div>
  );
};

export default DocumentsSection;